import {
  JsonFormat,
  arrayJsonFormat,
  booleanJsonFormat,
  numberJsonFormat,
  objectJsonFormat,
  stringJsonFormat,
  symbolJsonFormat,
  symbolUnionJsonFormat,
  urlJsonFormat,
} from "@redotech/json/format";
import { Json } from "@redotech/json/json";
import { bijectionRef } from "@redotech/util/bijection";
import { memoize } from "lodash";
import { ReturnType } from "./return";
import {
  Condition as Condition_,
  conditionJsonFormat as conditionJsonFormat_,
} from "./return-flow/condition";
import { ExchangeGroup } from "./team";

/** Adjustment for return value/upsell/restocking fees */
export interface Adjustment {
  flat: number;
  proportion: number;
}

export const adjustmentIdentity: Adjustment = { flat: 0, proportion: 1 };

const adjustmentJsonFormat: JsonFormat<Adjustment> = objectJsonFormat(
  { flat: numberJsonFormat, proportion: numberJsonFormat },
  {},
);

/** Credit type */
export enum CreditType {
  /** Discount code */
  DISCOUNT_CODE = "discount_code",
  /** Gift card */
  GIFT_CARD = "gift_card",
}

const creditTypeJsonFormat: JsonFormat<CreditType> = <JsonFormat<CreditType>>(
  stringJsonFormat
);

/**
 * Return flow
 */
export interface ReturnFlow {
  /** Steps */
  steps: Step[];
}

export interface StepIdFn {
  (key: StepFn): StepId;
}

export interface StepFn {
  (id: StepIdFn): Step;
}

export function stepsBuild(steps: Iterable<StepFn>): Step[] {
  const set = new Set(steps);
  let id: StepId = 0;
  const getId = memoize<(key: StepFn) => StepId>((step) => {
    if (!set.has(step)) {
      throw new Error(`Step is missing: ${step}`);
    }
    return id++;
  });
  const result: Step[] = [];
  for (const step of set) {
    const id = getId(step);
    result[id] = step(getId);
  }
  return result;
}

export const returnFlowJsonFormat: JsonFormat<ReturnFlow> = objectJsonFormat(
  { steps: arrayJsonFormat(bijectionRef(() => stepJsonFormat)) },
  {},
);

/** Multiple choice answer */
export interface MultipleChoiceAnswer {
  name: string;
  step: StepId;
}

/** Input answer part */
export interface InputAnswerPart {
  message: string;
  /** Text or list of URLs */
  value: string | URL[];
  type: InputPartType;
}

const inputAnswerPartJsonFormat: JsonFormat<InputAnswerPart> = objectJsonFormat(
  {
    message: stringJsonFormat,
    value: {
      read(json: Json) {
        return typeof json === "string"
          ? json
          : (<string[]>json).map((json) => new URL(json));
      },
      write(value: string | URL[]) {
        return typeof value === "string"
          ? value
          : value.map((value) => value.toString());
      },
    },
    type: bijectionRef(() => inputPartTypeJsonFormat),
  },
  {},
);

/** Input answer */
export interface InputAnswer {
  parts: InputAnswerPart[];
}

export const inputAnswerJsonFormat: JsonFormat<InputAnswer> = objectJsonFormat(
  { parts: arrayJsonFormat(inputAnswerPartJsonFormat) },
  {},
);

/**
 * Question option
 */
export interface MultipleChoiceOption {
  /** Name */
  name: string;
  /** Next step */
  next: StepId;
}

export const multipleChoiceOptionJsonFormat: JsonFormat<MultipleChoiceOption> =
  objectJsonFormat(
    {
      name: stringJsonFormat,
      next: bijectionRef(() => stepIdJsonFormat),
    },
    {},
  );

export type Payer =
  | typeof Payer.CUSTOMER
  | typeof Payer.MERCHANT
  | typeof Payer.REDO;

export namespace Payer {
  /** Customer */
  export const CUSTOMER = "customer";
  /** Merchant */
  export const MERCHANT = "merchant";
  /** Re:do */
  export const REDO = "redo";
}

export const payerJsonFormat: JsonFormat<Payer> = <JsonFormat<Payer>>(
  stringJsonFormat
);

// Null in case of green return when there is no label
export type UncoveredPayer =
  | typeof Payer.CUSTOMER
  | typeof Payer.MERCHANT
  | null;

export const uncoveredPayerJsonFormat: JsonFormat<UncoveredPayer> =
  stringJsonFormat as JsonFormat<UncoveredPayer>;

export interface ReturnOption {
  /** Return method */
  method: ReturnOptionMethod;
  /** Label payer */
  labelPayer: UncoveredPayer;
  /** Whether to ship the item back (i.e. not green return) */
  ship: boolean;
  /** Value of the return */
  adjustment: Adjustment;
}

export const returnOptionJsonFormat: JsonFormat<ReturnOption> =
  objectJsonFormat(
    {
      adjustment: adjustmentJsonFormat,
      labelPayer: uncoveredPayerJsonFormat,
      method: bijectionRef(() => returnOptionMethodJsonFormat),
      ship: booleanJsonFormat,
    },
    {},
  );

/**
 * Return method
 */
export type ReturnOptionMethod =
  | ReturnOptionMethod.Credit
  | ReturnOptionMethod.Exchange
  | ReturnOptionMethod.Refund;

export namespace ReturnOptionMethod {
  export const CREDIT = Symbol("credit");
  export const EXCHANGE = Symbol("exchange");
  export const REFUND = Symbol("refund");

  /**
   * Store credit
   */
  export interface Credit {
    type: typeof CREDIT;
    creditType: CreditType;
  }

  export const creditJsonFormat: JsonFormat<Credit> = objectJsonFormat(
    { type: symbolJsonFormat(CREDIT), creditType: creditTypeJsonFormat },
    {},
  );

  /**
   * Exchange
   */
  export interface Exchange {
    type: typeof EXCHANGE;
  }

  export const exchangeJsonFormat: JsonFormat<Exchange> = objectJsonFormat(
    { type: symbolJsonFormat(EXCHANGE) },
    {},
  );

  /**
   * Refund
   */
  export interface Refund {
    type: typeof REFUND;
  }

  export const refundJsonFormat: JsonFormat<Refund> = objectJsonFormat(
    { type: symbolJsonFormat(REFUND) },
    {},
  );
}

export const returnOptionMethodJsonFormat: JsonFormat<ReturnOptionMethod> =
  symbolUnionJsonFormat("type", "type", {
    [ReturnOptionMethod.CREDIT]: ReturnOptionMethod.creditJsonFormat,
    [ReturnOptionMethod.EXCHANGE]: ReturnOptionMethod.exchangeJsonFormat,
    [ReturnOptionMethod.REFUND]: ReturnOptionMethod.refundJsonFormat,
  });

export interface ClaimOption {
  /** Return method */
  method: ReturnOptionMethod;
  requireSendBack: boolean;
}

export const claimOptionJsonFormat: JsonFormat<ClaimOption> = objectJsonFormat(
  {
    method: returnOptionMethodJsonFormat,
    requireSendBack: booleanJsonFormat,
  },
  {},
);

/**
 * Input configuration
 */
export enum InputValidation {
  REQUIRED = "required",
  OPTIONAL = "optional",
}

export const inputValidationJson: JsonFormat<InputValidation> =
  stringJsonFormat as JsonFormat<InputValidation>;

/** Step */
export type Step =
  | Step.Condition
  | Step.Input
  | Step.MultipleChoice
  | Step.Reject
  | Step.Return
  | Step.ManualReview
  | Step.SubmitClaim
  | Step.Block;

export enum ExchangeTypeContext {
  VARIANT = "variant",
  ADVANCED = "advanced",
}

/** Exchange */
export interface ReturnExchange {
  /** Order details */
  order: any;
  /** Type */
  type: ExchangeTypeContext;
  /** The id of the new selected variant, if variant exchange */
  newVariantId: string | null;
  /** The exchange group used for the exchange, if there is one */
  exchangeGroup?: ExchangeGroup;
  /** The new item, if an exchange group was used */
  exchangeGroupItem?: ExchangeGroupItem;
}

/** Credit */
export interface ReturnCredit {
  amount: number;
  type: CreditType;
}

/** Refund */
export interface ReturnRefund {
  amount: number;
}

export interface ReturnLabel {
  /** Label payer */
  payer: Payer;
  /** Payment token, if paid by customer */
  paymentToken: string | null;
}

export enum ProvisionType {
  INSTANT = "instant",
  PROCESSED = "processed",
}

export interface Address {
  name: string;
  email: string;
  street1: string;
  street2: string;
  state: string;
  city: string;
  country: string;
  zip: string;
  phone: string;
  returnerName?: string;
  returnerEmail?: string;
}

export interface DiscountAllocation {
  discountedAmount: {
    amount: string;
    currencyCode: string;
  };
}

export interface GetFulfilledOrdersRequest {
  email?: string;
  order_number?: string;
  emailOrZip?: string;
  getAllStatuses?: boolean;
}
export interface ReturnRequest {
  /** Merchant charge to customer (non-negative); does not include shipping label */
  charge: string;
  /** The shipping fee total for all returns being created for return request */
  shippingFee: string;
  /** Shipping fee of an individual return */
  singleShippingFee: string;
  /** Whether the shipping fee is a flat rate or not */
  isFlatRate: boolean;
  /** Refund amount */
  refund: string;
  /** The total store credit, taking into account exchange fees */
  totalStoreCredit: string;
  /** The amount to collect from the customer if they fail to return for an instant exchange */
  returnCollectionHoldAmount: string | null;
  /** Return label, if there is one */
  label: ReturnLabel | null;
  /** Stripe payment token for an instant exchange */
  instantExchangePaymentToken: string | null;
  /** Items */
  items: ReturnItem[];
  /** Items from the advanced exchange cart */
  newItems: AdvancedExchangeItem[];
  /** Order ID */
  orderId: string;
  /** Status */
  status: ReturnRequestStatus;
  /** Provisioning */
  provision: ProvisionType;
  /** Address */
  shippingAddress: Address;
  /** Merchant Return Address */
  merchantAddress: Address | null; // null for rejected returns
  /** A stripe payment intent, just stored for reference after first return is created, and should not actually be charged. */
  orderPaymentIntent: any;
  /** The total value of the new order including taxes and discounts */
  newOrderValue: string;
  /** The taxes associated with a new order - created via advanced exchange */
  newOrderTaxes: string;
  /** The cart discount allocations from advanced exchange */
  discountAllocations: DiscountAllocation[];
  /** Whether or not the label price was taken from customers refund or store credit values */
  labelDeductedFromCredit: boolean;
  /** The idempotency key used to create the return */
  idempotencyKey: string;
  /* The kind of return to create
  		"return" | "claim" */
  returnType: ReturnType;
  /* The address to send any new items to (applies only to exchange orders)
      If undefined, this will default to the address set in shippingAddress */
  newOrderAddress?: Address;
}

export enum ReturnRequestStatus {
  PENDING = "pending",
  REJECTED = "rejected",
}

export const returnRequestStatus: JsonFormat<ReturnRequestStatus> = <
  JsonFormat<ReturnRequestStatus>
>stringJsonFormat;

export interface LabelResult {
  /** URL for postage label */
  postageUrl: URL;
  /** URL for QR code */
  qrCodeUrl: URL;
  /** The shipment carrier */
  carrier: string;
}

export const labelResultJson: JsonFormat<LabelResult> = objectJsonFormat(
  {
    postageUrl: urlJsonFormat,
    qrCodeUrl: urlJsonFormat,
    carrier: stringJsonFormat,
  },
  {},
);

export interface DraftOrder {
  url: URL;
}

export const draftOrderJsonFormat: JsonFormat<DraftOrder> = objectJsonFormat(
  { url: urlJsonFormat },
  {},
);

export interface ReturnResult {
  /** Draft order, if necessary */
  draftOrder: DraftOrder | null;
  /** Shipping label */
  label: LabelResult | null;
  creditType: CreditType;
  returnId: String;
}

export interface AdvancedExchangeItem {
  /** Variant ID */
  variantId: string;
  /** Product title */
  title: string;
  /** Variant title */
  variantTitle: string;
  /** The price of the variant */
  price: string;
  /** The total value of the item. */
  itemValue?: string;
  /** The quantity of the variant */
  quantity: number;
  /** The variant image urls */
  images: string[];
  /** The currency code */
  currencyCode: string;
  /** Custom attributes on the line item that we want to carry through to the order */
  attributes?: { key: string; value: string }[];
  /** The total tax amount of the new item */
  tax?: string;
}

export interface ExchangeGroupItem {
  /** Product ID */
  productId: string;
  /** Variant ID */
  variantId: string;
  /** Product title */
  title: string;
  /** Variant title */
  variantTitle: string;
  /** The variant image urls */
  imageSrc: string;
}

export interface ReturnItem {
  /** Variant exchange, if there is one */
  exchange: ReturnExchange | null;
  /** Input answers */
  inputAnswers: InputAnswer[];
  /** Requires manual review */
  manualReview: boolean;
  /** Multiple choice answers */
  multipleChoiceAnswers: MultipleChoiceAnswer[];
  /** ?? */
  variantId: string;
  /** Reason */
  reason: string | null;
  /** Refund, if there is one */
  refund: boolean;
  /** Whether item should be shipped (i.e. not green return) */
  ship: boolean;
  /** The added bonus or fee of the item being returned, if there is one */
  priceAdjustment: string | null;
  /** The price of the item before adjustment */
  price: string | null;
  /** The reason for rejecting the return, if rejected */
  rejectMessage?: string;
  productName: {
    title: string;
    variantTitle: string;
  };
}

export enum InputPartType {
  IMAGES = "images",
  TEXTAREA = "text_area",
}

export const inputPartTypeJsonFormat: JsonFormat<InputPartType> =
  stringJsonFormat as JsonFormat<InputPartType>;

export namespace Step {
  export const CONDITION = Symbol("condition");
  export const INPUT = Symbol("input");
  export const MANUAL_REVIEW = Symbol("manual_review");
  export const MULTIPLE_CHOICE = Symbol("multiple_choice");
  export const REJECT = Symbol("reject");
  export const RETURN = Symbol("return");
  export const SUBMIT_CLAIM = Symbol("submit_claim");
  export const BLOCK = Symbol("block");

  /**
   * Conditional
   */
  export interface Condition {
    type: typeof CONDITION;
    /** Expression */
    condition: Condition_;
    /** Next step if true */
    nextTrue: StepId;
    /** Next step if false */
    nextFalse: StepId;
  }

  export const conditionJsonFormat: JsonFormat<Condition> = objectJsonFormat(
    {
      type: symbolJsonFormat(CONDITION),
      condition: conditionJsonFormat_,
      nextTrue: bijectionRef(() => stepIdJsonFormat),
      nextFalse: bijectionRef(() => stepIdJsonFormat),
    },
    {},
  );

  export interface InputPart {
    // The main question for the input
    message: string;
    // Additional description for guiding the user through the question
    description: string;
    // Whether it is required or not, not sure what "none" should be, maybe we could make this a boolean named required?
    validation: InputValidation;
    type: InputPartType;
  }

  export const inputPartJson: JsonFormat<InputPart> = objectJsonFormat(
    {
      message: stringJsonFormat,
      description: stringJsonFormat,
      validation: inputValidationJson,
      type: inputPartTypeJsonFormat,
    },
    {},
  );

  /**
   * Customer input
   */
  export interface Input {
    type: typeof INPUT;
    /** Parts */
    parts: InputPart[];
    /** Next step */
    next: StepId;
  }

  export const inputJsonFormat: JsonFormat<Input> = objectJsonFormat(
    {
      type: symbolJsonFormat(INPUT),
      parts: arrayJsonFormat(inputPartJson),
      next: bijectionRef(() => stepIdJsonFormat),
    },
    {},
  );

  /**
   * Mark for manual review
   */
  export interface ManualReview {
    type: typeof MANUAL_REVIEW;
    /** Next step */
    next: StepId;
  }

  export const manualReviewJsonFormat: JsonFormat<ManualReview> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(MANUAL_REVIEW),
        next: bijectionRef(() => stepIdJsonFormat),
      },
      {},
    );

  /**
   * Customer multiple-choice question
   */
  export interface MultipleChoice {
    type: typeof MULTIPLE_CHOICE;
    /** Message */
    message: string;
    /** Options */
    options: MultipleChoiceOption[];
    /** Set the return reason from the answer to this question. */
    reason: boolean;
    /** Whether to shuffle the options */
    shuffle?: boolean;
  }

  export const multipleChoiceJsonFormat: JsonFormat<MultipleChoice> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(MULTIPLE_CHOICE),
        message: stringJsonFormat,
        options: arrayJsonFormat(multipleChoiceOptionJsonFormat),
        reason: booleanJsonFormat,
      },
      {
        shuffle: booleanJsonFormat,
      },
    );

  /**
   * Rejection
   */
  export interface Reject {
    type: typeof REJECT;
    /** Message */
    message: string;
  }

  export const rejectJsonFormat: JsonFormat<Reject> = objectJsonFormat(
    { type: symbolJsonFormat(REJECT), message: stringJsonFormat },
    {},
  );

  /**
   * Return
   */
  export interface Return {
    type: typeof RETURN;
    /** Options */
    options: ReturnOption[];
    /** Whether to use a flat shipping rate or not */
    flatShipping?: boolean;
    /** The value of the flat shipping rate, if flat shipping is enabled */
    flatShippingRate?: string;
  }

  export const returnJsonFormat: JsonFormat<Return> = objectJsonFormat(
    {
      type: symbolJsonFormat(RETURN),
      options: arrayJsonFormat(returnOptionJsonFormat),
    },
    {
      flatShipping: booleanJsonFormat,
      flatShippingRate: stringJsonFormat,
    },
  );

  /**
   * Submit Claim
   */
  export interface SubmitClaim {
    type: typeof SUBMIT_CLAIM;
    option: ClaimOption;
  }

  export const submitClaimJsonFormat: JsonFormat<SubmitClaim> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(SUBMIT_CLAIM),
        option: claimOptionJsonFormat,
      },
      {},
    );

  /**
   * Block
   */
  export interface Block {
    type: typeof BLOCK;
    message: string;
  }

  export const blockJsonFormat: JsonFormat<Block> = objectJsonFormat(
    {
      type: symbolJsonFormat(BLOCK),
      message: stringJsonFormat,
    },
    {},
  );
}

export const stepJsonFormat: JsonFormat<Step> = symbolUnionJsonFormat(
  "type",
  "type",
  {
    [Step.CONDITION]: Step.conditionJsonFormat,
    [Step.INPUT]: Step.inputJsonFormat,
    [Step.MANUAL_REVIEW]: Step.manualReviewJsonFormat,
    [Step.MULTIPLE_CHOICE]: Step.multipleChoiceJsonFormat,
    [Step.REJECT]: Step.rejectJsonFormat,
    [Step.RETURN]: Step.returnJsonFormat,
    [Step.SUBMIT_CLAIM]: Step.submitClaimJsonFormat,
    [Step.BLOCK]: Step.blockJsonFormat,
  },
);

/**
 * Step ID
 */
export type StepId = number;

export const stepIdJsonFormat: JsonFormat<StepId> = numberJsonFormat;
